import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { CardForm, CardFormProps } from "./CardForm";
import { PatchCardDto } from "@/types/v2";
import { ClassNameValue } from "tailwind-merge";
import { cn } from "@/lib/utils";

interface CardFormDialogProps<T> extends CardFormProps<T> {
  title?: string;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonClasses?: ClassNameValue;
  cardData?: PatchCardDto;
  description?: string;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
export const CardFormDialog = <T,>({
  title,
  buttonTitle,
  buttonIcon,
  buttonClasses,
  cardData,
  initialValues,
  onSubmit,
  description,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
  ...props
}: CardFormDialogProps<T>) => {
  const [isOpen, setIsOpen] = useState(open ?? false);

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  const handleSave = async (values: T) => {
    return onSubmit(values).finally(() => handleOpenChange(false));
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger ? (
        // Use custom trigger if provided
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Default trigger button
        !externalTrigger && (
          <DialogTrigger asChild>
            <Button
              className={cn(buttonClasses, "hover:opacity-100")}
              variant={"ghost-primary"}
              onClick={() => handleOpenChange(true)}
            >
              {buttonIcon}
              {buttonTitle ?? "Create Card"}
            </Button>
          </DialogTrigger>
        )
      )}

      <DialogContent className="max-h-[90vh] max-w-screen-sm overflow-auto">
        <DialogHeader>
          <DialogTitle>{title ?? "Create a New Card"}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {description ??
            "Create a new card. Fields here are determined by the card configuration."}
        </DialogDescription>
        <CardForm
          {...props}
          cardData={cardData}
          initialValues={initialValues}
          onSubmit={handleSave}
          onCancel={handleCancel}
        />
      </DialogContent>
    </Dialog>
  );
};
